import React, { useState, useEffect, useRef } from 'react';
import { Row, InputGroup, Form, Button, Table, Col, Card } from 'react-bootstrap';
import { CCol, CFormLabel, CFormFeedback, CFormInput } from '@coreui/react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PageWrapper from '../../pagecomponents/Common/PageWrapper.js';
import { getInvoiceReportNew, getInvoiceReportById, updateInvoice, printInvoiceReport } from '../../redux/slices/InvoiceSlice.js';
import DataTable from '../../pagecomponents/Common/DataTable.js';
import ModalWrapper from '../../pagecomponents/Common/ModalWrapper.js';
import InvoicePrintFormat from './Print.js';
import { checkEmpty, toastMessage, number_format } from '../../helpers/utility.js';
import { ToastContainer } from 'react-toastify';
import { InvoicePdfPage } from './SinglePrint.js';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import { getMasterData } from '../../redux/slices/CustomerSlice.js';
import useDebouncedValue from '../../helpers/useDebouncedValue';

const JOB_TYPE_PARAMS = {
  skip: 0,
  take: 2000,
  type: 'job_type',
  sorting: 'asc',
  sorting_key: 'created_at',
  filter: {},
};

const getInvoiceJobIds = (value) => {
  if (value === undefined || value === null) return [];

  const rawValue = String(value).trim();
  if (!rawValue) return [];

  return rawValue
    .split(/:{2,}/)
    .map((jobId) => jobId.trim())
    .filter(Boolean);
};

const InvoiceReportNew = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const pageName = 'Sales Report';
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [totalPage, setTotalPage] = useState(0);

  const PARAMS = {
    skip: 0,
    take: rowsPerPage,
    sorting: 'asc',
    sorting_key: 'invoice_Date',
    print: false,
    filter: {
      customer_Id: '',
      fromdate: '',
      todate: '',
      invoice_No: '',
      job_type: '',
      doc_number: '',
      invoice_search: '',
      customer_name: '',
    },
  };

  const [data, setData] = useState([]);
  const [summaryData, setSummaryData] = useState([]);
  const [printData, setPrintData] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [params, setParams] = useState(PARAMS);
  const [textFilters, setTextFilters] = useState({
    doc_number: '',
    invoice_search: '',
    customer_name: '',
  });
  const debouncedTextFilters = useDebouncedValue(textFilters, 400);
  // const [printParams, setPrintParams] = useState({ ...PARAMS });
  const [isLoading, setIsLoading] = useState(false);
  const [singlePrintData, setSinglePrintData] = useState([]);
  const [singleModalVisible, setSingleModalVisible] = useState(false);
  const [filterSectionVisible, setFilterSectionVisible] = useState(false);
  const [masterJobTypeData, setMasterJobTypeData] = useState([]);

  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);

  const getInvoiceById = async (invoice_Id) => {
    if (!invoice_Id) return null;

    try {
      const { payload } = await dispatch(getInvoiceReportById({ inputData: { invoice_Id } }));
      return payload?.data?.[0] || null;
    } catch (err) {
      toastMessage('error', 'Failed to fetch invoice details');
      return null;
    }
  };

  const handleInvoiceEdit = async (item) => {
    const refreshedItem = await getInvoiceById(item?.invoice_Id);
    if (!refreshedItem) {
      return;
    }

    setEditingInvoice(refreshedItem);
    setEditModalVisible(true);
  };

  // const prevPrintRef = useRef(params.print);

  useEffect(() => {
    fetchMasterData();
  }, []);

  useEffect(() => {
    if (!params.print) {
      getInvoiceReport();
    }
  }, [params.skip, params.take, params.sorting, params.sorting_key, JSON.stringify(params.filter)]);

  useEffect(() => {
    if (params.print) {
      (async () => {
        await fetchPrintData();
        setParams((prev) => ({ ...prev, print: false }));
      })();
    }
  }, [params.print]);

  useEffect(() => {
    setCurrentPage(1);
    setParams((prev) => {
      const prevFilter = prev?.filter || {};
      const isSameFilter =
        String(prevFilter.doc_number || '') === debouncedTextFilters.doc_number &&
        String(prevFilter.invoice_search || '') === debouncedTextFilters.invoice_search &&
        String(prevFilter.customer_name || '') === debouncedTextFilters.customer_name;

      if (isSameFilter && prev.skip === 0) {
        return prev;
      }

      return {
        ...prev,
        skip: 0,
        filter: {
          ...prevFilter,
          ...debouncedTextFilters,
        },
      };
    });
  }, [debouncedTextFilters]);

  // useEffect(() => {
  //     if (params.print !== prevPrintRef.current) {
  //         prevPrintRef.current = params.print;

  //         if (params.print === true) {
  //             fetchPrintData();
  //         } else {
  //             getInvoiceReport();
  //         }
  //     }

  // }, [params]);

  const handleResetFilters = () => {
    setTextFilters({
      doc_number: '',
      invoice_search: '',
      customer_name: '',
    });
    setParams((prev) => ({
      ...prev,
      print: false,
      skip: 0,
      take: rowsPerPage,
      filter: {
        ...prev.filter,
        fromdate: '',
        todate: '',
        job_type: '',
        doc_number: '',
        invoice_search: '',
        customer_name: '',
      },
    }));
  };

  const handlePrint = async () => {
    await getInvoiceReport();

    setParams((prev) => ({
      ...prev,
      print: true,
      skip: 0,
      take: Number.MAX_SAFE_INTEGER,
    }));
  };

  const fetchMasterData = async () => {
    try {
      const { payload } = await dispatch(getMasterData({ inputData: JOB_TYPE_PARAMS }));
      setMasterJobTypeData(payload?.job_type);
    } catch (err) {
      toastMessage('error', 'Failed to fetch data');
    } finally {
    }
  };

  const getInvoiceReport = async () => {
    setIsLoading(true);

    try {
      const { payload } = await dispatch(getInvoiceReportNew({ inputData: params }));
      setData(payload?.data);
      setSummaryData(payload?.summaryData);
      // console.log(payload?.summaryData);

      setTotalPage(Math.ceil(payload.count / rowsPerPage));
      return payload;
    } catch (err) {
      toastMessage('error', 'Failed to fetch data');
      return null;
    } finally {
      setIsLoading(false); // Stop loader
    }
  };

  const printModalDecider = async (item) => {
    const refreshedItem = await getInvoiceById(item?.invoice_Id);
    if (!refreshedItem) {
      return;
    }
    const jobIds = getInvoiceJobIds(refreshedItem?.job_Id);

    if (jobIds.length > 1) {
      openPrintModal(refreshedItem);
    } else {
      openSinglePrintModal(refreshedItem);
    }
  };

  const openPrintModal = (item) => {
    setModalVisible(true);
    setPrintData(item);
  };

  const closePrintModal = () => {
    setModalVisible(false);
    // setPrintData([]);
  };

  const openSinglePrintModal = (item) => {
    setSingleModalVisible(true);
    setSinglePrintData(item);
  };

  const closeSinglePrintModal = () => {
    setSingleModalVisible(false);
    // setSinglePrintData([]);
  };

  const handleInvoiceDelete = async (item) => {
    // console.log(item);
    let formData = {
      invoice_Id: item.invoice_Id,
      type: 'delete',
      customer_Id: item.customer_Id,
    };
    const { payload } = await dispatch(updateInvoice({ inputData: formData }));
    console.log(payload);
    if (!checkEmpty(payload?.msg)) {
      toastMessage(payload?.status, payload?.msg);
    }
    if (payload?.status === 'success') {
      await getInvoiceReport();
    }
  };

  //   const handleInvoiceEdit = async (item) => {
  //   // console.log(item);
  //   let formData = {
  //     invoice_Id: item.invoice_Id,
  //     customer_Id: item.customer_Id,
  //   };
  //   const { payload } = await dispatch(updateInvoice({ inputData: formData }));
  //   console.log(payload);
  //   if (!checkEmpty(payload?.msg)) {
  //     toastMessage(payload?.status, payload?.msg);
  //   }
  //   if (payload?.status === "success") {
  //     // setTimeout(() => {
  //     getInvoiceReport();
  //     // }, 1000);
  //   }
  // };

  // const handleSort = (key) => {
  //     setParams((prev) => ({
  //         ...prev,
  //         sorting_key: key,
  //         sorting: prev.sorting_key === key && prev.sorting === "asc" ? "desc" : "asc",
  //     }));
  // };

  const handleSort = (key) => {
    setCurrentPage(1);

    setParams((prev) => ({
      ...prev,
      skip: 0,
      sorting_key: key,
      sorting: prev.sorting_key === key && prev.sorting === 'asc' ? 'desc' : 'asc',
    }));
  };

  const handleRowsPerPageChange = (rows) => {
    setRowsPerPage(rows);
    setCurrentPage(1);

    setParams((prev) => ({
      ...prev,
      take: rows,
      skip: 0,
    }));
  };

  const setPage = (pagenum) => {
    setCurrentPage(pagenum);
    setParams((prev) => ({
      ...prev,
      skip: (pagenum - 1) * prev.take,
    }));
  };

  const goToCreate = () => {
    navigate('/invoice/create');
  };

  const columns = [
    { label: 'Invoice Date', accessor: 'invoice_Date', sortable: true },
    { label: 'Customer Name', accessor: 'customer_Name', sortable: true },
    { label: 'Job No', accessor: 'job_number', sortable: false },
    { label: 'Doc No', accessor: 'doc_number', sortable: false },
    { label: 'Payment Type', accessor: 'payment_Type', sortable: false },
    // {label: "Customer Name", accessor: "customer_Name", sortable: true},
    { label: 'Invoice No', accessor: 'invoice_No', sortable: true },
    { label: 'No of Vehicles', accessor: 'job_Load_No', sortable: false },
    { label: 'Cost of Revenue', accessor: 'costOfRev', sortable: false },
    { label: 'Revenue', accessor: 'revCharge', sortable: false },
    { label: 'Amount', accessor: 'invoice_Amount', sortable: true },
    // {label: "Action", accessor: "invoice_Amount", sortable: false},
  ];

  const toogleFilterSection = () => {
    setFilterSectionVisible((prev) => !prev);
  };

  const handleFilterParamsOnChange = (e, dateField = null, date = null) => {
    if (dateField) {
      setParams((prev) => ({
        ...prev,
        skip: 0,
        filter: {
          ...prev.filter,
          [dateField]: date ? format(new Date(date), 'yyyy-MM-dd') : '', // Convert date to 'YYYY-MM-DD'
        },
      }));
      setCurrentPage(1);
    } else {
      const { name, value } = e.target;
      if (['doc_number', 'invoice_search', 'customer_name'].includes(name)) {
        setTextFilters((prev) => ({
          ...prev,
          [name]: value,
        }));
        return;
      }
      setParams((prev) => ({
        ...prev,
        skip: 0,
        filter: {
          ...prev.filter,
          [name]: value,
        },
      }));
      setCurrentPage(1);
    }
  };

  const fetchPrintData = async () => {
    try {
      const { payload } = await dispatch(printInvoiceReport({ inputData: params }));
      console.log(payload);

      if (payload?.status === 'success') {
        if (payload?.pdfURL) {
          window.open(payload.pdfURL, '_blank');
        } else {
          toastMessage('error', payload.msg || 'File link not found');
        }
        setParams((prev) => ({
          ...prev,
          print: false,
        }));
      } else {
        toastMessage('error', payload.msg || 'Link not generated.');
      }
    } catch (err) {
      toastMessage('error', 'Failed to fetch data');
    }
  };

  return (
    <>
      <ToastContainer />
      <PageWrapper
        pageName={pageName}
        headerBtnTitle="+ Create"
        onHeaderButtonClick={() => {
          goToCreate();
        }}
        showFilterBtn={true}
        onFilterBtnClick={() => toogleFilterSection()}
        filterBtnTitle={filterSectionVisible ? 'Hide Filter' : 'Show Filter'}
        showAddBtn={true}
      >
        {filterSectionVisible && (
          <Row className="row-sm mb-3">
            <CCol md={3}>
              <CFormLabel className="main-content-label tx-11 tx-medium tx-gray-600" htmlFor="fromdate">
                From
              </CFormLabel>
              <InputGroup className="input-group reactdate-pic">
                <InputGroup.Text className="input-group-text">
                  <i className="typcn typcn-calendar-outline tx-24 lh--9 op-6"></i>
                </InputGroup.Text>
                <DatePicker
                  className="form-control"
                  name="fromdate"
                  id="fromdate"
                  selected={params.filter.fromdate ? new Date(params.filter.fromdate) : null} // Convert string to Date object
                  onChange={(date) => handleFilterParamsOnChange(null, 'fromdate', date)}
                  dateFormat="yyyy-MM-dd"
                />
              </InputGroup>
              <CFormFeedback invalid>Select From Date</CFormFeedback>
            </CCol>
            <CCol md={3}>
              <CFormLabel className="main-content-label tx-11 tx-medium tx-gray-600" htmlFor="todate">
                To
              </CFormLabel>
              <InputGroup className="input-group reactdate-pic">
                <InputGroup.Text className="input-group-text">
                  <i className="typcn typcn-calendar-outline tx-24 lh--9 op-6"></i>
                </InputGroup.Text>
                <DatePicker
                  className="form-control"
                  name="todate"
                  id="todate"
                  selected={params.filter.todate ? new Date(params.filter.todate) : null} // Convert string to Date object
                  onChange={(date) => handleFilterParamsOnChange(null, 'todate', date)}
                  dateFormat="yyyy-MM-dd"
                />
              </InputGroup>
              <CFormFeedback invalid>Select To Date</CFormFeedback>
            </CCol>
            <CCol md={3}>
              <CFormLabel className="main-content-label tx-11 tx-medium tx-gray-600" htmlFor="job_type">
                Mode
              </CFormLabel>
              <Form.Select
                className="form-control"
                id="job_type"
                name="job_type" // Ensure this matches PARAMS.filter.job_type
                value={params.filter.job_type} // Bind value
                onChange={handleFilterParamsOnChange} // Update on change
              >
                <option value="">Select</option>
                {masterJobTypeData?.map((option, index) => (
                  <option key={index} value={option.job_type_Code}>
                    {option.job_type_Name}
                  </option>
                ))}
              </Form.Select>
            </CCol>

            <CCol md={3}>
              <CFormLabel className="main-content-label tx-11 tx-medium tx-gray-600" htmlFor="job_type">
                Doc Number
              </CFormLabel>

              <CFormInput
                type="text"
                id="doc_number"
                name="doc_number" // Ensure this matches PARAMS.filter.job_type
                value={textFilters.doc_number} // Bind value
                onChange={handleFilterParamsOnChange} // Update on change
              />
            </CCol>

            <CCol md={3}>
              <CFormLabel className="main-content-label tx-11 tx-medium tx-gray-600" htmlFor="job_type">
                Invoice Number
              </CFormLabel>

              <CFormInput
                type="text"
                id="invoice_search"
                name="invoice_search" // Ensure this matches PARAMS.filter.job_type
                value={textFilters.invoice_search} // Bind value
                onChange={handleFilterParamsOnChange} // Update on change
              />
            </CCol>

            <CCol md={3}>
              <CFormLabel className="main-content-label tx-11 tx-medium tx-gray-600" htmlFor="job_type">
                Customer Name
              </CFormLabel>

              <CFormInput
                type="text"
                id="customer_name"
                name="customer_name" // Ensure this matches PARAMS.filter.job_type
                value={textFilters.customer_name} // Bind value
                onChange={handleFilterParamsOnChange} // Update on change
              />
            </CCol>

            <CCol
              md={2}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'left',
                paddingTop: '28px',
                paddingLeft: '20px',
              }}
            >
              <Row className="btn-animation">
                {/* <Button
                                    variant=""
                                    type="button"
                                    className={`btn btn-sm btn-primary ms-2`}
                                    onClick={() => {handleGetData()}}
                                >
                                    Get Data
                                </Button> */}
                <Button variant="" type="button" className="btn btn-sm btn-primary ms-2" onClick={handleResetFilters}>
                  Reset
                </Button>

                <Button variant="" type="button" className="btn btn-sm btn-info ms-2" onClick={handlePrint}>
                  Print
                </Button>
              </Row>
            </CCol>
          </Row>
        )}

        {!checkEmpty(data) && (
          <>
            <Row className="row-sm">
              <Col xl={3} lg={6} md={6} xm={12}>
                <Card className=" sales-card circle-image1">
                  <Row>
                    <div className="col-8">
                      <div className="ps-4 pt-4 pe-3 pb-4 pt-0">
                        <div className="">
                          <h6 className="mb-2 tx-12 ">TOTAL NO OF VEHICLE</h6>
                        </div>
                        <div className="pb-0 mt-0">
                          <div className="d-flex">
                            <h4 className="tx-26 font-weight-semibold mb-2">
                              {number_format(summaryData.invoice_Summary_TotalVehicleNumber, 2)}
                            </h4>
                          </div>
                          {/* <p className="mb-0 tx-12 text-muted">
                                                    Last week
                                                    <i className="fas fa-arrow-circle-up mx-2 text-success"></i>
                                                    <span className=" text-primary"> +427</span>
                                                </p> */}
                        </div>
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="circle-icon widget bg-primary-gradient text-center align-self-center shadow-primary overflow-hidden box-shadow-primary">
                        <i className="fe fe-truck tx-20 lh-lg text-white"></i>
                      </div>
                    </div>
                  </Row>
                </Card>
              </Col>
              <Col xl={3} lg={6} md={6} xm={12}>
                <Card className=" sales-card circle-image4">
                  <Row>
                    <div className="col-8">
                      <div className="ps-4 pt-4 pe-3 pb-4 pt-0">
                        <div className="">
                          <h6 className="mb-2 tx-12">TOTAL SALES</h6>
                        </div>
                        <div className="pb-0 mt-0">
                          <div className="d-flex">
                            <h4 className="tx-26 font-weight-semibold mb-2">
                              {number_format(summaryData.invoice_Summary_TotalAmount, 2)}
                            </h4>
                          </div>
                          {/* <p className="mb-0 tx-12  text-muted">
                                                    Last week
                                                    <i className="fas fa-arrow-circle-down mx-2 text-danger"></i>
                                                    <span className=" text-success"> -693</span>
                                                </p> */}
                        </div>
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="circle-icon widget bg-warning-gradient text-center align-self-center warning-success overflow-hidden box-shadow-warning">
                        <i className="fas fa-file-alt tx-20 lh-lg text-white"></i>
                      </div>
                    </div>
                  </Row>
                </Card>
              </Col>
              <Col xl={3} lg={6} md={6} xm={12}>
                <Card className=" sales-card circle-image3">
                  <Row>
                    <div className="col-8">
                      <div className="ps-4 pt-4 pe-3 pb-4 pt-0">
                        <div className="">
                          <h6 className="mb-2 tx-12">TOTAL COST OF SALES</h6>
                        </div>
                        <div className="pb-0 mt-0">
                          <div className="d-flex">
                            <h4 className="tx-26 font-weight-semibold mb-2">
                              {number_format(summaryData.invoice_Summary_CostofRevenue, 2)}
                            </h4>
                          </div>
                          {/* <p className="mb-0 tx-12 text-muted">
                                                    Last week
                                                    <i className="fas fa-arrow-circle-up mx-2 text-success"></i>
                                                    <span className=" text-primary"> +788</span>
                                                </p> */}
                        </div>
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="circle-icon widget bg-danger-gradient text-center align-self-center shadow-success overflow-hidden box-shadow-success">
                        <i className="mdi mdi-baby-buggy tx-20 lh-lg text-white"></i>
                      </div>
                    </div>
                  </Row>
                </Card>
              </Col>
              <Col xl={3} lg={6} md={6} xm={12}>
                <Card className=" sales-card circle-image2">
                  <Row>
                    <div className="col-8">
                      <div className="ps-4 pt-4 pe-3 pb-4 pt-0">
                        <div className="">
                          <h6 className="mb-2 tx-12">GROSS PROFIT</h6>
                        </div>
                        <div className="pb-0 mt-0">
                          <div className="d-flex">
                            <h4 className="tx-26 font-weight-semibold mb-2">
                              {number_format(summaryData.invoice_Summary_Revenue, 2)}
                            </h4>
                          </div>
                          {/* <p className="mb-0 tx-12 text-muted">
                                                    Last week
                                                    <i className="fas fa-arrow-circle-down mx-2 text-danger"></i>
                                                    <span className=" text-success"> -453</span>
                                                </p> */}
                        </div>
                      </div>
                    </div>
                    <div className="col-4">
                      <div className="circle-icon widget bg-success-gradient text-center align-self-center shadow-secondary overflow-hidden box-shadow-info">
                        <i className="fas fa-chart-line tx-20 lh-lg text-white"></i>
                      </div>
                    </div>
                  </Row>
                </Card>
              </Col>
            </Row>

            {/* <Row className="row-sm mb-3">
                        <CCol md={3}>
                            <Table className="table table-invoice table-bordered float-end">
                                <thead>
                                    <tr>
                                        <th colSpan={2} className="bg-dark text-white tx-left wd-20p">Account Summary</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>Total No of Vehicle</td>
                                        <td className="tx-right">{number_format(summaryData.invoice_Summary_TotalVehicleNumber,2)}</td>
                                    </tr>
                                    <tr>
                                        <td>Total Revenue</td>
                                        <td className="tx-right">{number_format(summaryData.invoice_Summary_Revenue,2)}</td>
                                    </tr>
                                    <tr>
                                        <td>Total Cost of Revenue</td>
                                        <td className="tx-right">{number_format(summaryData.invoice_Summary_CostofRevenue,2)}</td>
                                    </tr>
                                </tbody>
                                <tfoot className="border-bottom bg-dark text-white">
                                    <tr>
                                        <th>Total Bill Amount</th>
                                        <th className="tx-right">{number_format(summaryData.invoice_Summary_TotalAmount,2)}</th>
                                    </tr>
                                </tfoot>
                            </Table>
                        </CCol>
                    </Row> */}
          </>
        )}

        <DataTable
          columns={columns}
          data={data}
          pageName={pageName}
          onPrint={printModalDecider}
          onDelete={handleInvoiceDelete}
          onEdit={handleInvoiceEdit}
          editEnabled
          onSorting={handleSort}
          sortingState={{ key: params.sorting_key, order: params.sorting }}
          currentPage={currentPage}
          totalPages={totalPage}
          rowsPerPage={rowsPerPage}
          updateRowsPerPage={handleRowsPerPageChange}
          onPageChange={setPage}
          loader={isLoading}
        />

        <ModalWrapper visible={modalVisible} onClose={closePrintModal} modalTitle="Print Invoice" modalSize="lg">
          <InvoicePrintFormat printData={printData} />
        </ModalWrapper>

        {/* <button onClick={openSinglePrintModal}>Single Modal</button> */}

        <ModalWrapper visible={singleModalVisible} onClose={closeSinglePrintModal} modalTitle="" modalSize="lg">
          <InvoicePdfPage singlePrintData={singlePrintData} />
        </ModalWrapper>

        <ModalWrapper
          visible={editModalVisible}
          onClose={() => setEditModalVisible(false)}
          modalTitle="Edit Invoice"
          modalSize="md"
        >
          <InvoiceEditForm
            invoice={editingInvoice}
            onClose={() => setEditModalVisible(false)}
            onSaved={() => {
              setEditModalVisible(false);
              getInvoiceReport(); // refresh list after update
            }}
          />
        </ModalWrapper>
      </PageWrapper>
    </>
  );
};

const InvoiceEditForm = ({ invoice, onClose, onSaved }) => {
  const dispatch = useDispatch();
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    invoice_Id: invoice?.invoice_Id ?? '',
    customer_Id: invoice?.customer_Id ?? '',
    customer_Name: invoice?.customer_Name ?? '',
    invoice_Date: invoice?.invoice_Date ? String(invoice.invoice_Date).split(' ')[0] : '',
    invoice_No: invoice?.invoice_No ?? '',
    invoice_Mode: invoice?.invoice_Mode ?? '',
    job_Id: invoice?.job_Id ?? '',
    invoice_Remarks: invoice?.invoice_Remarks ?? '',
  });

  useEffect(() => {
    if (!invoice) return;
    setForm({
      invoice_Id: invoice?.invoice_Id ?? '',
      customer_Id: invoice?.customer_Id ?? '',
      customer_Name: invoice?.customer_Name ?? '',
      invoice_Date: invoice?.invoice_Date ? String(invoice.invoice_Date).split(' ')[0] : '',
      invoice_No: invoice?.invoice_No ?? '',
      invoice_Mode: invoice?.invoice_Mode ?? '',
      job_Id: invoice?.job_Id ?? '',
      invoice_Remarks: invoice?.invoice_Remarks ?? '',
    });
  }, [invoice]);

  if (!invoice) return null; // safety

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (checkEmpty(form.invoice_Date) || checkEmpty(form.invoice_No)) {
      toastMessage('error', 'Invoice Date and Invoice No are required');
      return;
    }

    const inputData = {
      invoice_Id: form.invoice_Id,
      invoice_Date: form.invoice_Date,
      invoice_No: form.invoice_No,
      invoice_Remarks: form.invoice_Remarks,
      customer_Id: form.customer_Id,
      job_Id: form.job_Id, // same string your controller already expects (job_Id, job_Id::job_Id, or job_Id:::job_Id)
      customer_Name: form.customer_Name,
      type: '', // normal update
      // if later you add in backend:
      // invoice_Mode: form.invoice_Mode,
    };

    try {
      setSubmitting(true);
      const { payload } = await dispatch(updateInvoice({ inputData }));

      if (!checkEmpty(payload?.msg)) {
        toastMessage(payload?.status, payload?.msg);
      }

      if (payload?.status === 'success') {
        onSaved?.(); // close + refresh list
      }
    } catch (err) {
      toastMessage('error', err?.message || 'Failed to update invoice');
    } finally {
      setSubmitting(false);
    }
  };

  // Convenience values for display-only bits
  const amount = invoice?.invoice_Amount ?? invoice?.invoice_Summary_TotalAmount ?? '';

  const docNumbers = Array.isArray(invoice?.doc_number) ? invoice.doc_number : [];
  const jobNumbers = Array.isArray(invoice?.job_number) ? invoice.job_number : [];
  const paymentTypes = Array.isArray(invoice?.payment_Type) ? invoice.payment_Type : [];

  return (
    <Form onSubmit={handleSubmit} autoComplete="off">
      {/* Row 1: IDs */}
      <Row className="mb-3">
        <Col md={6}>
          <CFormLabel>Invoice ID</CFormLabel>
          <Form.Control type="text" value={form.invoice_Id} disabled readOnly />
        </Col>
        <Col md={6}>
          <CFormLabel>Customer ID</CFormLabel>
          <Form.Control type="text" value={form.customer_Id} disabled readOnly />
        </Col>
      </Row>

      {/* Row 2: Customer / Mode */}
      <Row className="mb-3">
        <Col md={8}>
          <CFormLabel>Customer Name</CFormLabel>
          <Form.Control type="text" name="customer_Name" value={form.customer_Name} onChange={handleChange} />
        </Col>
        <Col md={4}>
          <CFormLabel>Mode</CFormLabel>
          <Form.Control
            type="text"
            name="invoice_Mode"
            value={form.invoice_Mode}
            onChange={handleChange}
            // currently not sent to backend unless you extend controller
          />
        </Col>
      </Row>

      {/* Row 3: Date / Invoice No */}
      <Row className="mb-3">
        <Col md={6}>
          <CFormLabel>Invoice Date</CFormLabel>
          <Form.Control type="date" name="invoice_Date" value={form.invoice_Date} onChange={handleChange} />
        </Col>
        <Col md={6}>
          <CFormLabel>Invoice No</CFormLabel>
          <Form.Control type="text" name="invoice_No" value={form.invoice_No} onChange={handleChange} />
        </Col>
      </Row>

      {/* Row 4: Job Id & Amount */}
      <Row className="mb-3">
        <Col md={8}>
          <CFormLabel>Job Id(s)</CFormLabel>
          <Form.Control
            type="text"
            name="job_Id"
            value={form.job_Id}
            onChange={handleChange}
            placeholder="jobId or jobId::jobId or jobId:::jobId"
          />
        </Col>
        <Col md={4}>
          <CFormLabel>Amount (read-only)</CFormLabel>
          <Form.Control type="text" value={amount !== '' ? `QAR ${number_format(amount, 2)}` : ''} disabled readOnly />
        </Col>
      </Row>

      {/* Row 5: Doc / Job / Payment info — display only */}
      <Row className="mb-3">
        <Col md={4}>
          <CFormLabel>Doc No(s)</CFormLabel>
          <div className="border rounded p-2" style={{ minHeight: 38 }}>
            {docNumbers.length ? (
              docNumbers.map((d, i) => <div key={i}>{d}</div>)
            ) : (
              <span className="text-muted">—</span>
            )}
          </div>
        </Col>
        <Col md={4}>
          <CFormLabel>Job No(s)</CFormLabel>
          <div className="border rounded p-2" style={{ minHeight: 38 }}>
            {jobNumbers.length ? (
              jobNumbers.map((d, i) => <div key={i}>{d}</div>)
            ) : (
              <span className="text-muted">—</span>
            )}
          </div>
        </Col>
        <Col md={4}>
          <CFormLabel>Payment Type(s)</CFormLabel>
          <div className="border rounded p-2" style={{ minHeight: 38 }}>
            {paymentTypes.length ? (
              paymentTypes.map((p, i) => <div key={i}>{p}</div>)
            ) : (
              <span className="text-muted">—</span>
            )}
          </div>
        </Col>
      </Row>

      {/* Row 6: Remarks */}
      <Row className="mb-3">
        <Col md={12}>
          <CFormLabel>Remarks</CFormLabel>
          <Form.Control
            as="textarea"
            rows={3}
            name="invoice_Remarks"
            value={form.invoice_Remarks}
            onChange={handleChange}
          />
        </Col>
      </Row>

      {/* Footer buttons */}
      <Row className="mt-3">
        <Col className="d-flex justify-content-end">
          <Button variant="secondary" className="me-2" type="button" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? 'Updating...' : 'Update'}
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

export default InvoiceReportNew;
